import { Box } from "@mui/material";
import * as React from "react";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { SaveDataObj } from "source/framework/saveData";
import { Dialog } from "source/components/framework/Dialog/Dialog";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { useEffect } from "react";
import { TaskDynamicFormContainer } from "source/components/task/TaskDynamicFormContainer";
import { TaskDynamicForm } from "source/components/task/TaskDynamicForm/TaskDynamicForm";

interface IChartTestDataRowDialogProp {
    item: ITaskDataRow;
    taskType: ITaskEditTypeItem;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: (cols: ITaskDataRowCol[]) => void;
    onClose: () => void;
    onRemove: (id: string) => void;
}

interface IChartTestDataRowDialogState {
    cols: ITaskDataRowCol[];
}

export const ChartTestDataRowDialog = (props: IChartTestDataRowDialogProp) => {
    const orgStateHash = React.useRef<string>("");

    const [state, setState] = React.useState<IChartTestDataRowDialogState>({
        cols: props.item.cols.map(i => new TaskDataRowCol(i)),
    });

    const getHashFromState = (state: IChartTestDataRowDialogState): string => {
        const result = new SaveDataObj();
        result.append("state.cols", state.cols);
        return result.hash;
    };
        
    const checkErrors = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            TaskTypeCol.validateInputOfRequiredTaskTypeCols(props.taskType.formCols, state.cols, null,
                (typeCol: ITaskTypeCol) => {
                    AppUtils.showErrorMessage(String.format(Translations.FieldParameterValueMustBeDefined, typeCol.title));
                    return resolve(false);
                });
            return resolve(true);
        });
    };

    const saveToDb = async (): Promise<boolean> => {
        if (!await AppUtils.validate(checkErrors)) return false;
        props.onSaved(state.cols);
        //Update state and hash
        orgStateHash.current = getHashFromState(state);
        return true;
    };

    useEffect(() => {
        orgStateHash.current = getHashFromState(state);
    }, []);

    useEffect(() => {
        const checkPendingSave = async () => {
            const cancelResult = await AppUtils.cancel(getHashFromState(state), orgStateHash.current, null);
            if (cancelResult === ConfirmationDialogResult.Cancel) return;
            if (cancelResult === ConfirmationDialogResult.Yes) {
                await saveToDb();
            }
            const newState = { cols: props.item.cols.map(i => new TaskDataRowCol(i)) };
            setState(newState);
            orgStateHash.current = getHashFromState(newState);
        };

        checkPendingSave();
    }, [props.item]);

    const handleSave = async (closeDialog: boolean) => {
        const result = await saveToDb();
        if (!result) return;
        if (!closeDialog) {
            props.onSaved(state.cols);
            return;
        }
        props.onClose();
    };

    const handleRemove = async () => {
        props.onRemove(props.item.id);
        props.onClose();
    };

    const handleCancel = async () => {
        const cancelResult = await AppUtils.cancel(getHashFromState(state), orgStateHash.current);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            handleSave(true);
        } else {
            props.onClose();
        }
    };

    const canMoveOut = async (): Promise<boolean> => {
        return await AppUtils.canMoveOut(getHashFromState(state), orgStateHash.current,
            saveToDb,
            null,
            (newStateHash) => { orgStateHash.current = newStateHash; }
        );
    };

    const handlePrevious = async () => {
        if (!await canMoveOut()) return;
        props.onPrevious();
    };

    const handleNext = async () => {
        if (!await canMoveOut()) return;
        props.onNext();
    };

    return (
        <Dialog
            id="chartTestDataRowDialog"
            maxWidth="lg"
            title={Translations.RowInformation}
            body={
                <Box display="flex" flexDirection="column">
                    <TaskDynamicFormContainer>
                        <TaskDynamicForm
                            isReadOnly={false}
                            isEdit={true}
                            taskTypeCols={props.taskType.formCols}
                            taskDataRowCols={state.cols}
                            onRefreshCols={(cols: ITaskDataRowCol[]) => setState((old) => ({ ...old, cols }))}
                        />
                    </TaskDynamicFormContainer>
                </Box>
            }
            actions={
                <Box display="flex" justifyContent="space-between" width="100%">
                    <DialogItemsNav
                        hasPrevious={props.hasPrevious}
                        hasNext={props.hasNext}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                    />
                    <DialogButtons
                        onCancel={handleCancel}
                        onRemove={handleRemove}
                        onSave={() => handleSave(false)}
                    />
                </Box>
            }
            onClose={handleCancel}
        />
    );
};